import jsPDF from 'jspdf';
import { PreApprovalData } from '@/types/pre-approval';
import { UserService } from '@/services';
import { formatCombinedGreeting, formatSmartGreeting } from './greetingHelpers';
import { formatLoanType, formatPropertyType, formatOccupancy } from './formatters';

interface TemplateElement {
  id: string;
  type: 'text' | 'image' | 'divider' | 'signature';
  content?: string;
  imageUrl?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  styles: {
    fontSize: number;
    fontFamily: string;
    fontWeight: 'normal' | 'bold';
    fontStyle: 'normal' | 'italic';
    textDecoration: 'none' | 'underline';
    textAlign: 'left' | 'center' | 'right';
    color: string;
    backgroundColor?: string;
    padding: number;
    borderRadius: number;
    border?: string;
    lineHeight?: number;
  };
}

interface VisualTemplate {
  id: string;
  name: string;
  pageSize: 'letter' | 'a4';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  elements: TemplateElement[];
}

// Inherited untyped contract: UserService.getTemplateVariables* and the borrower
// portal still exchange Record<string, any>; type those first, then tighten this.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TemplateProfileVariables = Record<string, any>;

const loadTemplateProfileVariables = async (
  loanOfficerId?: string,
  providedProfileVariables?: TemplateProfileVariables
): Promise<TemplateProfileVariables> => {
  if (providedProfileVariables) {
    return providedProfileVariables;
  }

  return loanOfficerId
    ? UserService.getTemplateVariablesForUser(loanOfficerId)
    : UserService.getTemplateVariables();
};

// Replace variables in text with actual values
export const replaceVisualTemplateVariables = async (
  text: string,
  data: PreApprovalData,
  profileData: TemplateProfileVariables
): Promise<string> => {
  // Handle TBD logic for property address
  const isTBD = data.propertyInfo?.address === 'TBD';
  const companyAddress = profileData.companyAddress || {};
  
  const variables: Record<string, string> = {
    // Borrower Information - Using smart greeting helper for consistent formatting
    borrowerGreeting: formatCombinedGreeting(data.borrowerInfo, data.coBorrowerInfo),
    borrowerNames: formatCombinedGreeting(data.borrowerInfo, data.coBorrowerInfo),
    borrowerFirstName: data.borrowerInfo?.firstName || '',
    borrowerLastName: data.borrowerInfo?.lastName || '',
    borrowerEmail: data.borrowerInfo?.email || '',
    borrowerPhone: data.borrowerInfo?.phone || '',
    coBorrowerGreeting: formatSmartGreeting(data.coBorrowerInfo),
    coBorrowerFirstName: data.coBorrowerInfo?.firstName || '',
    coBorrowerLastName: data.coBorrowerInfo?.lastName || '',
    coBorrowerEmail: data.coBorrowerInfo?.email || '',
    coBorrowerPhone: data.coBorrowerInfo?.phone || '',

    // Loan Information
    loanAmount: data.loanDetails.loanAmount ? `$${data.loanDetails.loanAmount.toLocaleString()}` : '',
    purchasePrice: data.loanDetails.purchasePrice ? `$${data.loanDetails.purchasePrice.toLocaleString()}` : '',
    loanType: formatLoanType(data.loanDetails.loanType || ''),
    downPaymentPercent: typeof data.loanDetails.downPaymentPercent === 'number' && Number.isFinite(data.loanDetails.downPaymentPercent)
      ? `${data.loanDetails.downPaymentPercent}%`
      : '',
    downPaymentAmount: typeof data.loanDetails.downPaymentAmount === 'number' && Number.isFinite(data.loanDetails.downPaymentAmount)
      ? `$${data.loanDetails.downPaymentAmount.toLocaleString()}`
      : '',
    ltv: data.calculations?.ltv ? `${data.calculations.ltv.toFixed(1)}%` : '',
    
    // Property Information
    propertyType: formatPropertyType(data.propertyInfo?.propertyType || ''),
    propertyOccupancy: formatOccupancy(data.propertyInfo?.occupancy || ''),
    propertyStreet: isTBD ? 'TBD' : (data.propertyInfo?.address || ''),
    propertyCity: isTBD ? '' : (data.propertyInfo?.city || ''),
    propertyState: isTBD ? '' : (data.propertyInfo?.state || ''),
    propertyZipCode: isTBD ? '' : (data.propertyInfo?.zipCode || ''),
    
    // Dates
    expirationDate: data.expirationDate || '',
    currentDate: new Date().toLocaleDateString(),
    dateOfPreApproval: new Date().toLocaleDateString(),
    
    // Loan Officer Information (from profile service)
    loanOfficerName: profileData.loanOfficerName,
    loanOfficerFirstName: profileData.loanOfficerFirstName,
    loanOfficerLastName: profileData.loanOfficerLastName,
    loanOfficerTitle: profileData.loanOfficerTitle,
    loanOfficerNMLS: profileData.loanOfficerNMLS,
    loanOfficerPhone: profileData.loanOfficerPhone,
    loanOfficerMobile: profileData.loanOfficerMobile,
    loanOfficerEmail: profileData.loanOfficerEmail,
    loanOfficerPhoto: profileData.loanOfficerPhoto || '',
    loanOfficerWebsite: profileData.companyWebsite, // Use company website for now
    loanOfficerSignature: data.loanOfficerSignatureDataUrl || profileData.loanOfficerName, // Use signature image if available, otherwise name
    
    // Company Information (from profile service)
    companyName: profileData.companyName,
    companyNMLS: profileData.companyNMLS,
    companyEmail: profileData.companyEmail,
    companyWebsite: profileData.companyWebsite,
    companyPhone: profileData.companyPhone,
    companyAddress: [
      companyAddress.street,
      companyAddress.city,
      [companyAddress.state, companyAddress.zip].filter(Boolean).join(' ')
    ].filter(Boolean).join(', '),
    companyStreet: companyAddress.street || '',
    companyCity: companyAddress.city || '',
    companyState: companyAddress.state || '',
    companyZip: companyAddress.zip || ''
  };

  // Mapping between bracket notation (human-readable) and curly notation (camelCase)
  const bracketToCurlyMapping: Record<string, string> = {
    // Borrower fields
    'Borrower Name': 'borrowerGreeting',
    'Borrower Names': 'borrowerNames',
    'Co-Borrower Name': 'coBorrowerGreeting',
    'First Name': 'borrowerFirstName',
    'Last Name': 'borrowerLastName',

    // Loan Officer fields
    'Loan Officer Name': 'loanOfficerName',
    'Loan Officer Title': 'loanOfficerTitle',
    'Loan Officer NMLS': 'loanOfficerNMLS',
    NMLS: 'loanOfficerNMLS',
    Title: 'loanOfficerTitle',
    Phone: 'loanOfficerPhone',
    Mobile: 'loanOfficerMobile',
    'Mobile Phone': 'loanOfficerMobile',
    Email: 'loanOfficerEmail',
    Website: 'companyWebsite',

    // Company fields
    'Company Name': 'companyName',
    'Company NMLS': 'companyNMLS',
    'Company NMLS#': 'companyNMLS',
    'Company Phone': 'companyPhone',
    'Company Email': 'companyEmail',
    'Company Website': 'companyWebsite',
    'Company Address': 'companyAddress',
    'Company Street': 'companyStreet',
    'Company City': 'companyCity',
    'Company State': 'companyState',
    'Company ZIP': 'companyZip',

    // Loan fields
    'Loan Amount': 'loanAmount',
    'Purchase Price': 'purchasePrice',
    'Loan Type': 'loanType',
    'Down Payment': 'downPaymentAmount',
    'Down Payment %': 'downPaymentPercent',
    'Down Payment Percent': 'downPaymentPercent',
    LTV: 'ltv',

    // Property fields
    'Property Address': 'propertyStreet',
    'Property Type': 'propertyType',
    'Property Occupancy': 'propertyOccupancy',
    'Property City': 'propertyCity',
    'Property State': 'propertyState',
    'Property ZIP': 'propertyZipCode',
    ZIP: 'propertyZipCode',

    // Date fields
    'Current Date': 'currentDate',
    'Expiration Date': 'expirationDate'
  };

  // Replace all variables in the text - supporting BOTH {{curly}} and [bracket] notation
  let processedText = text;

  Object.entries(variables).forEach(([key, value]) => {
    // Replace {{curly}} notation
    const curlyRegex = new RegExp(`{{${key}}}`, 'g');
    processedText = processedText.replace(curlyRegex, value);
  });

  // Replace [bracket] notation using mapping
  Object.entries(bracketToCurlyMapping).forEach(([bracketName, curlyKey]) => {
    if (variables[curlyKey]) {
      const bracketRegex = new RegExp(`\\[${bracketName}\\]`, 'g');
      processedText = processedText.replace(bracketRegex, variables[curlyKey]);
    }
  });

  return processedText;
};

// Process text with proper line breaks and paragraph spacing
const processTextLines = (doc: jsPDF, text: string, maxWidth: number): Array<{text: string, isParagraphBreak: boolean}> => {
  const processedLines: Array<{text: string, isParagraphBreak: boolean}> = [];
  
  // Split by line breaks first
  const textLines = text.split('\n');
  
  textLines.forEach((line) => {
    const trimmedLine = line.trim();
    
    // Check if this is a paragraph break (empty line)
    if (trimmedLine === '') {
      // Add paragraph break marker if not at the beginning
      if (processedLines.length > 0) {
        processedLines.push({ text: '', isParagraphBreak: true });
      }
      return;
    }
    
    // Word wrap the line if it's too long
    const wrappedLines = wrapTextLine(doc, trimmedLine, maxWidth);
    
    wrappedLines.forEach((wrappedLine) => {
      processedLines.push({ 
        text: wrappedLine, 
        isParagraphBreak: false 
      });
    });
  });
  
  return processedLines;
};

// Wrap a single line of text to fit within a width
const wrapTextLine = (doc: jsPDF, text: string, maxWidth: number): string[] => {
  const lines: string[] = [];
  const words = text.split(' ');
  let currentLine = '';

  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const lineWidth = doc.getTextWidth(testLine);

    if (lineWidth > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [''];
};

// Validate base64 image data
const isValidImageData = (imageUrl: string): boolean => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return false;
  }
  
  // Check if it's a data URL
  if (!imageUrl.startsWith('data:image/')) {
    return true; // For external URLs, we'll handle them separately
  }
  
  // Check if it has the proper base64 structure
  const base64Part = imageUrl.split(',')[1];
  if (!base64Part) {
    return false;
  }
  
  // Basic base64 validation
  try {
    // Check if it's valid base64 and not empty
    const decoded = atob(base64Part);
    return decoded.length > 0;
  } catch {
    return false;
  }
};

// Get image format from data URL
const getImageFormat = (imageUrl: string): string | null => {
  if (!imageUrl.startsWith('data:image/')) {
    return null;
  }
  
  const mimeType = imageUrl.split(',')[0].split(':')[1].split(';')[0];
  
  switch (mimeType) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'JPEG';
    case 'image/png':
      return 'PNG';
    case 'image/gif':
      return 'GIF';
    case 'image/webp':
      return 'WEBP';
    case 'image/svg+xml':
      return 'SVG'; // Mark as SVG for special handling
    default:
      return null;
  }
};

export const generateVisualPreApprovalPDF = async (
  template: VisualTemplate,
  preApproval: PreApprovalData,
  loanOfficerId?: string,
  profileVariables?: TemplateProfileVariables
): Promise<jsPDF> => {
  // Draw margin guides for debugging (comment out in production)
  const DEBUG_BOUNDARIES = false; // Set to false for production
  
  // Create PDF with proper page size
  const doc = new jsPDF({
    orientation: template.orientation,
    unit: 'pt',
    format: template.pageSize
  });

  // Set up page margins (jsPDF doesn't have built-in margin support)
  // We'll use these to offset our elements if needed
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  if (DEBUG_BOUNDARIES && template.margins) {
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.5);
    doc.setLineDashPattern([2, 2], 0);
    
    // Draw margin rectangle
    doc.rect(
      template.margins.left,
      template.margins.top,
      pageWidth - template.margins.left - template.margins.right,
      pageHeight - template.margins.top - template.margins.bottom,
      'S' // Stroke only
    );
    
    // Reset line style
    doc.setLineDashPattern([], 0);
  }

  const profileData = await loadTemplateProfileVariables(
    loanOfficerId,
    profileVariables || preApproval.publicProfileVariables
  );

  // Process text replacements for all elements first
  const processedElements = await Promise.all(
    template.elements.map(async (element) => {
      if (element.type === 'text' && element.content) {
        return {
          ...element,
          content: await replaceVisualTemplateVariables(element.content, preApproval, profileData)
        };
      }
      return element;
    })
  );

  // Process each element
  processedElements.forEach(element => {
    // Use coordinates directly (no conversion needed as builder uses points)
    const x = element.x;
    const y = element.y;
    const width = element.width;
    const height = element.height;


    switch (element.type) {
      case 'text': {
        if (!element.content) {
break;
}

        // Check if content is a base64 image (signature)
        if (element.content.startsWith('data:image')) {
          try {
            doc.addImage(element.content, 'PNG', x, y, width, height);
          } catch {
            // If image fails to load, show fallback text
            doc.setFontSize(10);
            doc.setTextColor('#666666');
            doc.text('[Signature]', x + width / 2, y + height / 2, { align: 'center' });
          }
          break;
        }

        // Use pre-processed text
        const text = element.content;

        // Set text properties
        doc.setFontSize(element.styles.fontSize);
        doc.setTextColor(element.styles.color);

        // Handle font weight and style
        let fontStyle = 'normal';
        if (element.styles.fontWeight === 'bold' && element.styles.fontStyle === 'italic') {
          fontStyle = 'bolditalic';
        } else if (element.styles.fontWeight === 'bold') {
          fontStyle = 'bold';
        } else if (element.styles.fontStyle === 'italic') {
          fontStyle = 'italic';
        }
        doc.setFont('helvetica', fontStyle);

        // Calculate text position based on alignment and padding
        const padding = element.styles.padding;
        let textX = x + padding;
        
        if (element.styles.textAlign === 'center') {
          textX = x + width / 2;
        } else if (element.styles.textAlign === 'right') {
          textX = x + width - padding;
        }

        // Draw element boundary for debugging (comment out in production)
        if (DEBUG_BOUNDARIES) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.rect(x, y, width, height, 'S');
        }

        // Handle background color if present
        if (element.styles.backgroundColor && element.styles.backgroundColor !== 'transparent') {
          doc.setFillColor(element.styles.backgroundColor);
          doc.rect(x, y, width, height, 'F');
        }

        // Process text with proper line breaks and paragraph spacing
        const maxTextWidth = width - (padding * 2);
        const processedLines = processTextLines(doc, text, maxTextWidth);
        
        // Calculate line heights
        const baseLineHeight = element.styles.fontSize * (element.styles.lineHeight || 1.8); // Increased from 1.5
        const paragraphSpacing = baseLineHeight * 0.8; // Extra space for paragraph breaks
        
        // Calculate the font's ascent (approximate)
        // For Helvetica, ascent is roughly 0.72 of the font size
        const fontAscent = element.styles.fontSize * 0.72;
        
        // Position text from top of element + padding + font ascent
        let textY = y + padding + fontAscent;

        // Draw each line of text
        processedLines.forEach((lineData) => {
          if (lineData.isParagraphBreak) {
            // Add extra space for paragraph breaks
            textY += paragraphSpacing;
            return;
          }
          
          const line = lineData.text;
          // Handle text decoration (underline)
          if (element.styles.textDecoration === 'underline') {
            const lineWidth = doc.getTextWidth(line);
            let underlineX = textX;
            
            if (element.styles.textAlign === 'center') {
              underlineX = textX - lineWidth / 2;
            } else if (element.styles.textAlign === 'right') {
              underlineX = textX - lineWidth;
            }
            
            doc.setDrawColor(element.styles.color);
            doc.line(
              underlineX,
              textY + 2,
              underlineX + lineWidth,
              textY + 2
            );
          }

          doc.text(line, textX, textY, {
            align: element.styles.textAlign
          });
          
          textY += baseLineHeight;
        });
        break;
      }

      case 'image': {
        if (!element.imageUrl || element.imageUrl.trim() === '') {
          // Draw placeholder for missing images
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(1);
          doc.rect(x, y, width, height, 'S');
          doc.setFontSize(10);
          doc.setTextColor(150, 150, 150);
          doc.text('Image Placeholder', x + width / 2, y + height / 2, { align: 'center' });
          break;
        }

        // Validate image data
        if (!isValidImageData(element.imageUrl)) {
          // Draw error placeholder for invalid image data
          doc.setDrawColor(255, 200, 200);
          doc.setLineWidth(1);
          doc.rect(x, y, width, height, 'S');
          doc.setFontSize(9);
          doc.setTextColor(200, 50, 50);
          doc.text('Invalid Image', x + width / 2, y + height / 2 - 5, { align: 'center' });
          doc.text('Format', x + width / 2, y + height / 2 + 5, { align: 'center' });
          break;
        }

        try {
          // For base64 images, we can add them directly
          if (element.imageUrl.startsWith('data:image')) {
            // Detect and validate image format from data URL
            const format = getImageFormat(element.imageUrl);
            if (format === 'SVG') {
              // Special handling for SVG files
              doc.setDrawColor(255, 165, 0);
              doc.setLineWidth(2);
              doc.rect(x, y, width, height, 'S');
              doc.setFontSize(10);
              doc.setTextColor(255, 165, 0);
              doc.text('SVG Not Supported', x + width / 2, y + height / 2 - 10, { align: 'center' });
              doc.text('Please convert to', x + width / 2, y + height / 2, { align: 'center' });
              doc.text('PNG or JPEG', x + width / 2, y + height / 2 + 10, { align: 'center' });
            } else if (format) {
              doc.addImage(element.imageUrl, format, x, y, width, height);
            } else {
              throw new Error('Unsupported image format');
            }
          } else {
            // For URL images, show placeholder since we can't load them synchronously
            doc.setDrawColor(100, 150, 255);
            doc.setLineWidth(1);
            doc.rect(x, y, width, height, 'S');
            doc.setFontSize(9);
            doc.setTextColor(100, 150, 255);
            doc.text('External Image', x + width / 2, y + height / 2 - 5, { align: 'center' });
            doc.text('(URL)', x + width / 2, y + height / 2 + 5, { align: 'center' });
          }
        } catch (error) {
          console.error('Failed to add image to PDF:', error);
          // Draw detailed error placeholder
          doc.setDrawColor(255, 200, 200);
          doc.setLineWidth(1);
          doc.rect(x, y, width, height, 'S');
          doc.setFontSize(8);
          doc.setTextColor(200, 50, 50);
          doc.text('Image Error:', x + width / 2, y + height / 2 - 10, { align: 'center' });
          doc.text('Corrupt or', x + width / 2, y + height / 2, { align: 'center' });
          doc.text('Unsupported', x + width / 2, y + height / 2 + 10, { align: 'center' });
        }
        break;
      }

      case 'divider': {
        doc.setDrawColor(element.styles.backgroundColor || '#000000');
        doc.setLineWidth(height);
        doc.line(x, y + height / 2, x + width, y + height / 2);
        break;
      }

      case 'signature': {
        // Check if actual signature exists
        if (preApproval.loanOfficerSignatureDataUrl &&
            (preApproval.hasLoanOfficerSignature || preApproval.loanOfficerSignatureDataUrl)) {
          try {
            // Get image dimensions to preserve aspect ratio
            const img = new Image();
            img.src = preApproval.loanOfficerSignatureDataUrl;

            // Calculate dimensions preserving aspect ratio (matching preview behavior)
            const maxWidth = width;
            const maxHeight = height - 25; // Reserve space for metadata
            const imgAspect = img.width / img.height;
            const containerAspect = maxWidth / maxHeight;

            let renderWidth, renderHeight;
            if (imgAspect > containerAspect) {
              // Image is wider - constrain by width
              renderWidth = maxWidth;
              renderHeight = maxWidth / imgAspect;
            } else {
              // Image is taller - constrain by height
              renderHeight = maxHeight;
              renderWidth = maxHeight * imgAspect;
            }

            // Center the image in the container
            const centerX = x + (maxWidth - renderWidth) / 2;
            const centerY = y + (maxHeight - renderHeight) / 2;

            // Add signature image to PDF with preserved aspect ratio
            doc.addImage(
              preApproval.loanOfficerSignatureDataUrl,
              'PNG',
              centerX,
              centerY,
              renderWidth,
              renderHeight
            );

            // Add signature metadata (date and IP) below image
            if (preApproval.loanOfficerSignatureDate || preApproval.loanOfficerSignatureIpAddress) {
              doc.setFontSize(8);
              doc.setTextColor('#374151');
              let metadataY = y + height - 20;

              if (preApproval.loanOfficerSignatureDate) {
                const signedDate = new Date(preApproval.loanOfficerSignatureDate);
                const formattedDate = signedDate.toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
                doc.text(`Signed on: ${formattedDate}`, x + width / 2, metadataY, { align: 'center' });
                metadataY += 10;
              }

              if (preApproval.loanOfficerSignatureIpAddress) {
                doc.text(`IP: ${preApproval.loanOfficerSignatureIpAddress}`, x + width / 2, metadataY, { align: 'center' });
              }
            }
          } catch (error) {
            console.error('Failed to add signature image to PDF:', error);
            // Fall back to placeholder if image fails
            doc.setDrawColor('#000000');
            doc.setLineWidth(1);
            doc.line(x, y + height - 10, x + width, y + height - 10);
            doc.setFontSize(10);
            doc.setTextColor('#666666');
            doc.text('Signature (Image Load Failed)', x, y + height);
          }
        } else {
          // No signature - show placeholder
          doc.setDrawColor('#9ca3af');  // Gray line
          doc.setLineWidth(2);
          doc.line(x, y + height - 10, x + width, y + height - 10);

          doc.setFontSize(10);
          doc.setTextColor('#666666');
          doc.text('Loan Officer E-Signature (Pending)', x, y + height);
        }
        break;
      }
    }
  });

  return doc;
};

export const downloadVisualPreApprovalPDF = async (
  template: VisualTemplate,
  preApproval: PreApprovalData,
  loanOfficerId?: string,
  profileVariables?: TemplateProfileVariables
) => {
  const doc = await generateVisualPreApprovalPDF(template, preApproval, loanOfficerId, profileVariables);
  const fileName = `Pre-Approval_${preApproval.borrowerInfo.lastName}_${preApproval.borrowerInfo.firstName}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

export const getVisualPreApprovalPDFBlob = async (
  template: VisualTemplate,
  preApproval: PreApprovalData,
  loanOfficerId?: string,
  profileVariables?: TemplateProfileVariables
): Promise<Blob> => {
  const doc = await generateVisualPreApprovalPDF(template, preApproval, loanOfficerId, profileVariables);
  return doc.output('blob');
};

export const getVisualPreApprovalPDFDataUrl = async (
  template: VisualTemplate,
  preApproval: PreApprovalData
): Promise<string> => {
  const doc = await generateVisualPreApprovalPDF(template, preApproval);
  return doc.output('datauristring');
};
